import React, { createContext, useContext, useState, useEffect } from "react";

import { apiFetch, setGlobalLogoutHandler, setGlobalAuthToken, setOnTokensRefreshed } from "../utils/apiUtils";
import { User, AuthContextType, Subscription, AuthSessionResponse, GoogleAuthResult, AppleAuthResult, SsoEntryPoint } from "../types/auth";
import { settleFirmMembershipFromSession } from "../services/firmMembershipStore";
import {
  signInWithApple,
  clearStoredAppleProfile,
  AppleSignInError,
  AppleCredential,
} from "../services/appleIdentity";
import { trackLogin, trackLogout, trackSignup, trackOtpVerified, identifyUser, clearUser } from "../utils/analytics";
import { capture } from "../analytics/posthog";
import { EVENTS } from "../analytics/events";
import { fetchBetaStatus } from "../services/betaService";
import { subscriptionService } from "../services/subscriptionService";
import { disableGoogleAutoSelect } from "../services/googleIdentity";
import { computeIsPro } from "../utils/proAccess";
import { forgetReturnUrl } from "../utils/appHandOff";
import { resetBillingAccounts } from "../services/billingAccountsStore";
import { clearDemoSession, demoExitUrl, demoLocation, getDemoSession, isDemo } from "../config/demo";
import { purgeAllFieldSnapshots } from "../components/fieldService/offline/fieldSnapshots";
import { stopFieldPush } from "../components/fieldService/offline/fieldPush";
import { clearAllFieldQueues, parkFieldQueues } from "../components/fieldService/offline/fieldWriteQueue";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const EMAIL_KEY = "email";
const UUID_KEY = "user_uuid";
const PHONE_KEY = "phone_number";
const FIRST_NAME_KEY = "first_name";
const LAST_NAME_KEY = "last_name";
const SESSION_EXPIRATION_KEY = "session_expiration";
const SUBSCRIPTION_KEY = "subscription";
const FORCE_PAYWALL_LOCAL = process.env.REACT_APP_FORCE_PAYWALL_LOCAL === "true";

const getDefaultLocalSubscription = (): Subscription | undefined =>
  FORCE_PAYWALL_LOCAL ? { status: "none" } : undefined;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [isBeta, setIsBeta] = useState(false);
  const [betaRank, setBetaRank] = useState<number | null>(null);
  const [betaTotal, setBetaTotal] = useState<number | null>(null);

  const isPro = user ? computeIsPro(user.subscription) : false;

  // Set up global logout handler on mount: the API refused the refresh
  // token, so the session has ended without the person choosing to leave.
  useEffect(() => {
    setGlobalLogoutHandler(expireSession);

    // When token refresh succeeds, persist the new tokens
    setOnTokensRefreshed(async (newAccessToken: string, newRefreshToken?: string) => {
      try {
        localStorage.setItem(STORAGE_KEY, newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        }
      } catch {
        // Swallow — tokens are already in memory
      }
      setUser((prev) =>
        prev ? { ...prev, authToken: newAccessToken } : prev
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update global auth token when user changes
  useEffect(() => {
    const token = user?.authToken || null;
    setGlobalAuthToken(token);
  }, [user?.authToken]);

  // Load stored auth data on startup
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const storedToken = localStorage.getItem(STORAGE_KEY);
        const storedEmail = localStorage.getItem(EMAIL_KEY);
        const storedUuid = localStorage.getItem(UUID_KEY);
        const storedPhone = localStorage.getItem(PHONE_KEY);
        const storedFirstName = localStorage.getItem(FIRST_NAME_KEY);
        const storedLastName = localStorage.getItem(LAST_NAME_KEY);
        const storedSubscriptionRaw = localStorage.getItem(SUBSCRIPTION_KEY);
        const storedSubscription: Subscription | undefined = storedSubscriptionRaw
          ? JSON.parse(storedSubscriptionRaw)
          : getDefaultLocalSubscription();

        // The token is the session; an email is optional. A phone-only
        // technician (PRD 89 §4.13) has none, and requiring one signed them
        // out on every reload. A dead token is caught by the check below.
        if (storedToken) {
          setUser({
            email: storedEmail || undefined,
            phone_number: storedPhone || undefined,
            authToken: storedToken,
            uuid: storedUuid || undefined,
            first_name: storedFirstName || undefined,
            last_name: storedLastName || undefined,
            subscription: storedSubscription,
          });

          // Set global tokens immediately to avoid race conditions
          setGlobalAuthToken(storedToken);

          // The app draws nothing until this check is done, so nothing waits
          // on anything it does not need. Beta status is asked alongside
          // /home rather than after it: only a waitlist user needs it, but
          // ProtectedRoute reads it on its first render. It is applied only
          // once /home has said the session is good.
          const betaStatusRequest = fetchBetaStatus().catch(() => null);

          // Validate token with retry logic and more lenient handling
          const validateTokenWithRetry = async (attempt: number = 1, maxAttempts: number = 3): Promise<boolean> => {
            try {
              const response = await apiFetch("home", {
                method: "GET",
                headers: {
                  "Authorization": `Bearer ${storedToken}`,
                  "Content-Type": "application/json",
                },
              });

              if (response.status === 401) {
                // Token is definitively invalid
                return false;
              }

              // Parse updated subscription from home response if available.
              // /home carries the subscription and queues the provider sync
              // itself, so the boot makes no POST /subscription/sync of its
              // own (that only queued a second SyncSubscriptionJob per load).
              if (response.ok) {
                try {
                  const data = await response.json();
                  const updates: Partial<User> = {};
                  if (data.subscription) {
                    localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify(data.subscription));
                    updates.subscription = data.subscription;
                  }
                  if (data.payment_required !== undefined) {
                    updates.payment_required = data.payment_required;
                  }
                  // The path that actually matters: this runs on every page
                  // load and the app blocks on it, so the answer is in hand
                  // before the sidebar or the landing redirect exists to want
                  // it (PRD 40 §5.6.1).
                  if (data.belongs_to_firm !== undefined) {
                    settleFirmMembershipFromSession(
                      storedUuid || undefined,
                      data.belongs_to_firm,
                      data.practice_workflow,
                      data.firm_role,
                    );
                  }
                  if (Object.keys(updates).length > 0) {
                    setUser((prev) => prev ? { ...prev, ...updates } : prev);
                  }
                } catch {
                  // Non-critical — subscription stays as stored
                }

                // Beta status on startup so waitlist users stay on /waitlist
                // after refresh. A failure leaves it false (non-critical).
                const betaStatus = await betaStatusRequest;
                if (betaStatus?.beta) {
                  setIsBeta(true);
                  if (betaStatus.rank != null) setBetaRank(betaStatus.rank);
                  if (betaStatus.total != null) setBetaTotal(betaStatus.total);
                }
              }

              return true;
            } catch (error) {
              if (attempt >= maxAttempts) {
                return true;
              }

              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 3000);
              await new Promise(resolve => setTimeout(resolve, delay));

              return validateTokenWithRetry(attempt + 1, maxAttempts);
            }
          };

          try {
            const isValid = await validateTokenWithRetry();
            if (!isValid) {
              await expireSession();
            }
          } catch {
            // If validation completely fails, keep the user logged in
            // The network interceptor will handle actual 401 responses from API calls
          }
        }
      } catch (error) {
        console.warn("Error loading stored auth:", error);
      } finally {
        setLoading(false);
      }
    };

    loadStoredAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storeAuthData = async (token: string, email?: string, refreshToken?: string, uuid?: string, firstName?: string, lastName?: string, subscription?: Subscription, phoneNumber?: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, token);
      if (email) localStorage.setItem(EMAIL_KEY, email);
      if (phoneNumber) localStorage.setItem(PHONE_KEY, phoneNumber);
      if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      if (uuid) localStorage.setItem(UUID_KEY, uuid);
      if (firstName) localStorage.setItem(FIRST_NAME_KEY, firstName);
      if (lastName) localStorage.setItem(LAST_NAME_KEY, lastName);
      if (subscription) localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify(subscription));
    } catch (error) {
      console.warn("Error storing auth data:", error);
    }
  };

  const clearAuthData = async () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(EMAIL_KEY);
      localStorage.removeItem(UUID_KEY);
      localStorage.removeItem(PHONE_KEY);
      localStorage.removeItem(FIRST_NAME_KEY);
      localStorage.removeItem(LAST_NAME_KEY);
      localStorage.removeItem(SESSION_EXPIRATION_KEY);
      localStorage.removeItem(SUBSCRIPTION_KEY);
    } catch (error) {
      console.warn("Error clearing auth data:", error);
    }
    // The mobile hand-off's link signs the app in as whoever it was made for.
    // It must not outlive this session and wait for the next person who signs
    // in on this tab and reaches /subscribe.
    forgetReturnUrl();
    // What this person pays for (the badge, /subscription) is theirs alone.
    resetBillingAccounts();
  };

  const persistSubscription = async (subscription?: Subscription) => {
    try {
      if (subscription) {
        localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify(subscription));
      } else {
        localStorage.removeItem(SUBSCRIPTION_KEY);
      }
    } catch {
      // Persistence failed — continue with in-memory state
    }
  };

  const verifySubscriptionAfterLogin = async (): Promise<Subscription | undefined> => {
    const result = await subscriptionService.syncSubscriptionStatus();

    if (!result.ok) {
      return undefined;
    }

    return result.subscription ?? { status: "none" };
  };

  // `quiet` leaves `subscriptionLoading` alone: ProtectedRoute draws its
  // full-page loading while that is true, which unmounts the whole app under
  // it. Quiet is for a refresh made while the app is on screen (a business
  // purchase landing on the billing card); the load at sign-in blocks.
  const refreshSubscriptionFromApi = async ({ quiet = false }: { quiet?: boolean } = {}): Promise<void> => {
    if (!quiet) setSubscriptionLoading(true);
    try {
      const result = await subscriptionService.syncSubscriptionStatus();
      if (!result.ok) {
        return;
      }

      const subscription = result.subscription ?? { status: "none" };
      await persistSubscription(subscription);
      setUser((prev) => prev ? { ...prev, subscription } : prev);
    } finally {
      if (!quiet) setSubscriptionLoading(false);
    }
  };


  // Turns an authenticated API response into a live session. Shared by OTP
  // verification and Google sign-in: both endpoints return the same body, so
  // both must persist, identify and hydrate it identically.
  const establishSession = async (
    data: AuthSessionResponse,
    persistEmail?: string,
    phoneNumber?: string
  ): Promise<void> => {
    const initialSubscription: Subscription | undefined = data.subscription || getDefaultLocalSubscription();

    // Set the global token before any await so the calls below already carry it.
    setGlobalAuthToken(data.access_token);
    setUser({
      email: persistEmail || undefined,
      phone_number: phoneNumber || undefined,
      first_name: data.first_name || undefined,
      last_name: data.last_name || undefined,
      authToken: data.access_token,
      uuid: data.user_uuid || undefined,
      subscription: initialSubscription,
      payment_required: data.payment_required,
    });

    // The sidebar's Practice section and the "/" landing rule both need this
    // before their first render (PRD 40 §5.6.1). Delivering it here, from a
    // response the session already had to make, is what keeps either from
    // paying for a request of its own. Undefined means the API predates the
    // flag — say nothing, and the store falls back to asking.
    if (data.belongs_to_firm !== undefined) {
      settleFirmMembershipFromSession(
        data.user_uuid,
        data.belongs_to_firm,
        data.practice_workflow,
        data.firm_role,
      );
    }

    if (data.user_uuid) identifyUser(data.user_uuid, persistEmail);
    await storeAuthData(
      data.access_token,
      persistEmail,
      data.refresh_token,
      data.user_uuid,
      data.first_name,
      data.last_name,
      initialSubscription,
      phoneNumber
    );

    setSubscriptionLoading(true);
    try {
      const verifiedSubscription = await verifySubscriptionAfterLogin();
      if (verifiedSubscription) {
        await persistSubscription(verifiedSubscription);
        setUser((prev) => prev ? { ...prev, subscription: verifiedSubscription } : prev);
      }
    } finally {
      setSubscriptionLoading(false);
    }

    if (data.beta) {
      setIsBeta(true);
      try {
        const betaData = await fetchBetaStatus();
        if (betaData.beta) {
          setBetaRank(betaData.rank ?? null);
          setBetaTotal(betaData.total ?? null);
        }
      } catch {
        // Beta status check failed, so waitlist position stays unknown
      }
    }
  };

  const registerWithEmail = async (email: string, firstName?: string, lastName?: string): Promise<{
    ok: boolean;
    error?: string;
    autoVerified?: boolean;
    accessToken?: string;
    refreshToken?: string;
    userUuid?: string;
  }> => {
    try {
      const response = await apiFetch("register_email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, first_name: firstName, last_name: lastName }),
        skipAuth: true,
      });
      if (response.ok) {
        trackSignup();
        const contentType = response.headers.get("Content-Type") || "";
        if (contentType.includes("application/json")) {
          const data = await response.json();
          // Development/simulator: API auto-verifies and returns tokens directly
          if (data.status === "auto_verified" && data.access_token) {
            const initialSubscription: Subscription | undefined = data.subscription || getDefaultLocalSubscription();
            setGlobalAuthToken(data.access_token);
            setUser({
              email,
              authToken: data.access_token,
              uuid: data.user_uuid || undefined,
              subscription: initialSubscription,
            });
            await storeAuthData(data.access_token, email, data.refresh_token, data.user_uuid, undefined, undefined, initialSubscription);
            return {
              ok: true,
              autoVerified: true,
              accessToken: data.access_token,
              refreshToken: data.refresh_token,
              userUuid: data.user_uuid,
            };
          }
        }
        return { ok: true };
      }
      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        return { ok: false, error: data.error || (Array.isArray(data.errors) ? data.errors[0] : undefined) };
      }
      return { ok: false };
    } catch {
      return { ok: false, error: "Unable to reach the API. Please confirm localhost services are running and try again." };
    }
  };

  const loginWithEmail = async (email: string): Promise<{ ok: boolean; error?: string; otpPhone?: string; status?: number }> => {
    try {
      const response = await apiFetch("login_email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        skipAuth: true,
      });
      if (response.ok) {
        const data = await response.json();
        trackLogin("email");
        // API returns { phone_number: ... } when OTP is sent via SMS
        return { ok: true, otpPhone: data.phone_number || undefined };
      }
      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        return { ok: false, status: response.status, error: data.error || (Array.isArray(data.errors) ? data.errors[0] : undefined) };
      }
      return { ok: false, status: response.status };
    } catch {
      return { ok: false };
    }
  };

  const loginWithPhone = async (phoneNumber: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const response = await apiFetch("login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: phoneNumber }),
        skipAuth: true,
      });
      if (response.ok) {
        trackLogin("phone");
        return { ok: true };
      }
      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        return { ok: false, error: data.error || (Array.isArray(data.errors) ? data.errors[0] : undefined) };
      }
      return { ok: false };
    } catch {
      return { ok: false };
    }
  };

  const registerWithPhone = async (phoneNumber: string, firstName?: string, lastName?: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const response = await apiFetch("register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: phoneNumber, first_name: firstName, last_name: lastName }),
        skipAuth: true,
      });
      if (response.ok) {
        return { ok: true };
      }
      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        return { ok: false, error: data.error || (Array.isArray(data.errors) ? data.errors[0] : undefined) };
      }
      return { ok: false };
    } catch {
      return { ok: false };
    }
  };

  const verifyOtp = async (otp: string, firstName?: string, lastName?: string, email?: string, phoneNumber?: string, sessionEmail?: string): Promise<{ ok: boolean; error?: string; accessToken?: string; refreshToken?: string; userUuid?: string; beta?: boolean; subscriptionActive?: boolean }> => {
    try {
      const response = await apiFetch("verify_otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email || undefined,
          phone_number: phoneNumber || undefined,
          otp,
          first_name: firstName,
          last_name: lastName,
        }),
        skipAuth: true,
      });

      if (response.ok) {
        const data = await response.json();

        if (!data.access_token) {
          console.error("verifyOtp: No access_token in response", data);
          return { ok: false, error: data.message || "No access token received" };
        }

        // Use sessionEmail (the email user entered) for persistence,
        // falling back to the email used for API verification
        const persistEmail = sessionEmail || email;
        await establishSession(data, persistEmail, phoneNumber);

        trackOtpVerified();
        capture(EVENTS.APP_AUTHENTICATED, { method: "session" });

        return { ok: true, accessToken: data.access_token, refreshToken: data.refresh_token, userUuid: data.user_uuid, beta: data.beta ?? false, subscriptionActive: data.subscription_active };
      }
      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        return { ok: false, error: data.error || (Array.isArray(data.errors) ? data.errors[0] : undefined) };
      }
      return { ok: false };
    } catch {
      // OTP verification error
      return { ok: false };
    }
  };

  /**
   * Exchanges the Google ID token from the "Sign in with Google" button for a
   * Greenback session. The token proves nothing on its own, because the API
   * verifies it, so this only has to relay it and interpret the answer.
   *
   * The API replies with either a session or the same `verification_sent`
   * challenge the email flow uses, which is how an account with SMS two-factor
   * still gets its second factor after signing in with Google.
   */
  const loginWithGoogle = async (
    credential: string,
    entryPoint: SsoEntryPoint
  ): Promise<GoogleAuthResult> => {
    /*
      No sso_sign_in_started or _abandoned on this path, and it is a gap rather
      than an oversight: Google renders its button inside an iframe we cannot
      see into, so there is no click to observe and a user who closes the
      account chooser is indistinguishable from one who never opened it. By the
      time this function runs, Google has already authorised. Closing it needs
      GIS's `click_listener` on renderButton, which is a separate change.

      The failure legs below are still worth having: they are everything that
      goes wrong after Google says yes, which the API cannot report either
      because no request ever reaches it.
    */
    try {
      const response = await apiFetch("auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: credential }),
        skipAuth: true,
      });

      const contentType = response.headers.get("Content-Type") || "";
      const data = contentType.includes("application/json") ? await response.json() : null;

      if (!response.ok) {
        capture(EVENTS.SSO_SIGN_IN_FAILED, {
          provider: "google",
          entry_point: entryPoint,
          reason: "api_rejected",
          status: response.status,
        });
        return {
          ok: false,
          error: data?.error || (Array.isArray(data?.errors) ? data.errors[0] : undefined),
        };
      }

      if (data?.status === "verification_sent") {
        return {
          ok: true,
          otpRequired: true,
          otpPhone: data.phone_number || undefined,
          email: data.email || undefined,
        };
      }

      if (!data?.access_token) {
        capture(EVENTS.SSO_SIGN_IN_FAILED, {
          provider: "google",
          entry_point: entryPoint,
          reason: "no_session",
        });
        return { ok: false, error: "Google sign-in did not return a session. Please try again." };
      }

      const session = data as AuthSessionResponse;
      await establishSession(session, session.email);

      if (session.new_user) {
        trackSignup();
      } else {
        trackLogin("google");
      }
      capture(EVENTS.APP_AUTHENTICATED, { method: "google" });

      return {
        ok: true,
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        userUuid: session.user_uuid,
        email: session.email,
        beta: session.beta ?? false,
        subscriptionActive: session.subscription_active,
        newUser: session.new_user ?? false,
      };
    } catch {
      capture(EVENTS.SSO_SIGN_IN_FAILED, {
        provider: "google",
        entry_point: entryPoint,
        reason: "network",
      });
      return { ok: false, error: "Unable to reach the API. Please try again." };
    }
  };

  /**
   * Runs Apple's popup and exchanges the identity token for a Greenback
   * session. The counterpart to loginWithGoogle, and the same reasoning: the
   * token proves nothing on its own, because the API verifies it.
   *
   * Two things are Apple's alone. The popup is owned here rather than by the
   * button, because Apple's script hands the token straight back to the caller
   * instead of invoking a callback the way Google's does. And the name travels
   * beside the token: Apple puts none in it, and releases it only on the user's
   * very first authorization, so the local copy is cleared only once the API
   * has accepted the sign-in.
   */
  const loginWithApple = async (
    entryPoint: SsoEntryPoint
  ): Promise<AppleAuthResult> => {
    // Unlike Google, the popup is owned here, so the click and the dismissal
    // are both observable and this path gets the full funnel.
    capture(EVENTS.SSO_SIGN_IN_STARTED, {
      provider: "apple",
      entry_point: entryPoint,
    });

    let credential: AppleCredential;
    try {
      credential = await signInWithApple();
    } catch (error) {
      if (error instanceof AppleSignInError) {
        const props = {
          provider: "apple",
          entry_point: entryPoint,
          reason: error.reason,
        };
        if (error.cancelled) {
          capture(EVENTS.SSO_SIGN_IN_ABANDONED, props);
          return { ok: false, cancelled: true };
        }
        capture(EVENTS.SSO_SIGN_IN_FAILED, props);
        return { ok: false, error: error.message };
      }
      capture(EVENTS.SSO_SIGN_IN_FAILED, {
        provider: "apple",
        entry_point: entryPoint,
        reason: "unknown",
      });
      return { ok: false, error: "Apple sign-in failed. Please try again." };
    }

    try {
      const response = await apiFetch("auth/apple", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identity_token: credential.identityToken,
          // Exchanged server-side for the refresh token that account deletion
          // later needs to revoke with Apple. It expires in about five minutes.
          ...(credential.authorizationCode ? { authorization_code: credential.authorizationCode } : {}),
          // Present on the first authorization only, and omitted rather than
          // sent as null so the API can tell "not supplied" from "cleared".
          ...(credential.firstName ? { first_name: credential.firstName } : {}),
          ...(credential.lastName ? { last_name: credential.lastName } : {}),
        }),
        skipAuth: true,
      });

      const contentType = response.headers.get("Content-Type") || "";
      const data = contentType.includes("application/json") ? await response.json() : null;

      if (!response.ok) {
        capture(EVENTS.SSO_SIGN_IN_FAILED, {
          provider: "apple",
          entry_point: entryPoint,
          reason: "api_rejected",
          status: response.status,
        });
        return {
          ok: false,
          error: data?.error || (Array.isArray(data?.errors) ? data.errors[0] : undefined),
        };
      }

      if (!data?.access_token) {
        capture(EVENTS.SSO_SIGN_IN_FAILED, {
          provider: "apple",
          entry_point: entryPoint,
          reason: "no_session",
        });
        return { ok: false, error: "Apple sign-in did not return a session. Please try again." };
      }

      // The API has the profile now, so the one-shot copy Apple gave us has
      // done its job. Anything short of this leaves it in place, which is the
      // whole reason it is kept: a retry after a failure still has a name.
      clearStoredAppleProfile();

      const session = data as AuthSessionResponse;
      await establishSession(session, session.email);

      if (session.new_user) {
        trackSignup();
      } else {
        trackLogin("apple");
      }
      capture(EVENTS.APP_AUTHENTICATED, { method: "apple" });

      return {
        ok: true,
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        userUuid: session.user_uuid,
        email: session.email,
        beta: session.beta ?? false,
        subscriptionActive: session.subscription_active,
        newUser: session.new_user ?? false,
      };
    } catch {
      capture(EVENTS.SSO_SIGN_IN_FAILED, {
        provider: "apple",
        entry_point: entryPoint,
        reason: "network",
      });
      return { ok: false, error: "Unable to reach the API. Please try again." };
    }
  };

  const loginWithTokens = async (accessToken: string, refreshToken: string | undefined, email: string | undefined, uuid: string | undefined): Promise<void> => {
    const initialSubscription: Subscription | undefined = getDefaultLocalSubscription();
    setGlobalAuthToken(accessToken);
    setUser({
      email,
      authToken: accessToken,
      uuid,
      subscription: initialSubscription,
    });
    if (uuid && email) identifyUser(uuid, email);
    await storeAuthData(accessToken, email, refreshToken, uuid, undefined, undefined, initialSubscription);

    // Fetch beta status now that the user is authenticated
    try {
      const betaData = await fetchBetaStatus();
      if (betaData.beta) {
        setIsBeta(true);
        setBetaRank(betaData.rank ?? null);
        setBetaTotal(betaData.total ?? null);
      }
    } catch {
      // Beta status check failed — keep defaults
    }
  };

  const checkAuth = async (): Promise<boolean> => {
    try {
      if (!user?.authToken) {
        await expireSession();
        return false;
      }

      const response = await apiFetch("home", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${user.authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        await expireSession();
        return false;
      }
      return response.ok;
    } catch {
      // For checkAuth, be more lenient - only logout if we get a definitive 401
      // Network errors or other failures should not immediately logout the user
      // The network interceptor will handle 401 responses from actual API calls
      return true;
    }
  };

  const refreshBetaStatus = async () => {
    try {
      const data = await fetchBetaStatus();
      setIsBeta(data.beta);
      setBetaRank(data.rank ?? null);
      setBetaTotal(data.total ?? null);
    } catch {
      // Refresh failed — keep current state
    }
  };

  const refreshSubscriptionStatus = async (options?: { quiet?: boolean }) => {
    try {
      await refreshSubscriptionFromApi(options);
    } catch {
      // Refresh failed — keep current state
    }
  };

  const setSubscriptionStatus = async (subscription?: Subscription) => {
    await persistSubscription(subscription);
    setUser((prev) => (prev ? { ...prev, subscription } : prev));
  };

  // A Live sample session that has ended: cleared, then "start again". Not
  // when the visitor is already on a /demo/ page: arriving at /demo/trades
  // with last week's token retires that token on load, and must not bounce
  // them off the page that is about to start a new one.
  const endDemoSession = async () => {
    await clearSession();
    clearDemoSession();
    if (!window.location.pathname.startsWith("/demo/")) demoLocation.assign("/demo/ended");
  };

  // Signing out of the Live sample returns the visitor to the marketing page
  // they came from. POST /logout is the one write the demo api allows; it only
  // deletes this token, and a failure changes nothing for the visitor.
  //
  // Reached with no token left, this is the tail of an ended session (the 401
  // handler above already cleared it and other callers still call logout()),
  // so it goes to the same "start again" page rather than off the site.
  const demoSignOut = async () => {
    const persona = getDemoSession()?.persona;
    if (!localStorage.getItem(STORAGE_KEY)) {
      await endDemoSession();
      return;
    }
    try {
      await apiFetch("/logout", { method: "POST" });
    } catch {
      // Best effort: the token expires on its own within two hours.
    }
    await clearSession();
    clearDemoSession();
    demoLocation.assign(demoExitUrl(persona));
  };

  const logout = async () => {
    if (isDemo()) {
      await demoSignOut();
      return;
    }
    await clearSession();
  };

  // The session ended without the person choosing to leave: the API refused
  // the refresh token, or the session's time ran out. Unlike logout(), any
  // field presses still waiting to be sent are kept for this person's next
  // sign-in (PRD 108 §4.2).
  const expireSession = async () => {
    // The Live sample's two hours are up (or the nightly rebuild retired the
    // token): there is no refresh token and no login page to send anyone
    // to, so they get "start again" instead (PRD 117, D3).
    if (isDemo()) {
      await endDemoSession();
      return;
    }
    await clearSession({ keepUnsentFieldWrites: true });
  };

  const clearSession = async ({ keepUnsentFieldWrites = false }: { keepUnsentFieldWrites?: boolean } = {}) => {
    // Without this Google silently re-signs the user in on the next visit,
    // which makes "log out" look like it did nothing.
    disableGoogleAutoSelect();
    // A shared phone keeps no copy of anyone's day (PRD 95 §4.13), and stops
    // buzzing for whoever left (PRD 108) — while the session can still say so.
    await stopFieldPush();
    await purgeAllFieldSnapshots();
    if (keepUnsentFieldWrites) parkFieldQueues();
    else clearAllFieldQueues();
    await clearAuthData();
    setUser(null);
    setSubscriptionLoading(false);
    setGlobalAuthToken(null);
    setIsBeta(false);
    setBetaRank(null);
    setBetaTotal(null);
    trackLogout();
    clearUser();
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <AuthContext.Provider value={{
      user,
      registerWithEmail,
      loginWithEmail,
      registerWithPhone,
      loginWithPhone,
      verifyOtp,
      loginWithGoogle,
      loginWithApple,
      loginWithTokens,
      logout,
      expireSession,
      checkAuth,
      refreshBetaStatus,
      refreshSubscriptionStatus,
      setSubscriptionStatus,
      loading,
      subscriptionLoading,
      isBeta,
      betaRank,
      betaTotal,
      isPro,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Hook for components that require authentication
export const useAuthProtection = () => {
  const { user, logout, checkAuth } = useAuth();

  useEffect(() => {
    const verifyAuth = async () => {
      if (!user?.authToken) {
        await logout();
        return;
      }

      const isAuthValid = await checkAuth();
      if (!isAuthValid) {
        await logout();
        return;
      }
    };

    verifyAuth();
  }, [user, logout, checkAuth]);

  return {
    isAuthenticated: !!user?.authToken,
    user,
    logout
  };
};

// HOC for protecting components that require authentication
export const withAuthProtection = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated } = useAuthProtection();

    if (!isAuthenticated) {
      return null; // Component won't render if not authenticated
    }

    return <Component {...props} />;
  };
};
